// =============================================================================
// CIT computation — shared between the client-side preview and the audit/UI.
// =============================================================================
// This MUST match supabase/migrations/153_update_post_cit_accrual_atomic.sql
// exactly. Change both files together.
//
// Rules encoded here:
//   • Adjustments: assessable_profit = max(0, ytdPbt + totalDisallowables - totalAllowables)
//     CIT and EDT are applied to assessable_profit, not raw PBT.
//   • Company size is classified from YTD turnover only.
//     small  : turnover ≤ small threshold
//     medium : small threshold < turnover ≤ medium threshold
//     large  : turnover > medium threshold
//   • CIT rate by size: cit_rate_small / cit_rate_medium / cit_rate_large
//   • EDT (TET) rate:   tet_rate_small for small + medium, tet_rate_large for large
//   • Loss handling: if assessable_profit ≤ 0, CIT = 0 and EDT = 0
//   • Minimum tax (0.5% of turnover): applies to medium and large ONLY.
//     Small companies are EXEMPT.
//   • Total YTD charge: greater of (CIT + EDT) or minimum tax
//   • Rounding: ROUND(..., 2). JS Math.round on (x*100)/100 mirrors PG ROUND(...,2)
//     for the non-negative values we deal with here.
// =============================================================================

export type CompanySize = 'small' | 'medium' | 'large'

export interface BusinessTaxRatesInput {
  cit_turnover_small_threshold: number
  cit_turnover_medium_threshold: number
  cit_rate_small: number
  cit_rate_medium: number
  cit_rate_large: number
  tet_rate_small: number
  tet_rate_large: number
  minimum_tax_rate: number
}

export interface CitComputationInput {
  ytdProfitBeforeTax: number
  ytdTurnover: number
  totalDisallowables: number
  totalAllowables: number
  priorYtdAccrued: number
  settings: BusinessTaxRatesInput
}

export interface CitComputationResult {
  companySize: CompanySize
  citRatePercent: number
  edtRatePercent: number
  minimumTaxRatePercent: number

  totalDisallowables: number
  totalAllowables: number
  assessableProfit: number   // max(0, ytdPbt + totalDisallowables - totalAllowables)

  citAmount: number
  edtAmount: number
  minimumTaxAmount: number
  totalTaxCharge: number     // greater of (CIT + EDT) or minimum tax

  priorYtdAccrued: number
  periodCharge: number       // totalTaxCharge - priorYtdAccrued (can be negative)
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

export function classifyCompanySize(
  ytdTurnover: number,
  settings: BusinessTaxRatesInput,
): CompanySize {
  if (ytdTurnover <= settings.cit_turnover_small_threshold) return 'small'
  if (ytdTurnover <= settings.cit_turnover_medium_threshold) return 'medium'
  return 'large'
}

export function computeCit(input: CitComputationInput): CitComputationResult {
  const {
    ytdProfitBeforeTax,
    ytdTurnover,
    totalDisallowables,
    totalAllowables,
    priorYtdAccrued,
    settings,
  } = input

  const assessableProfit = Math.max(
    0,
    round2(ytdProfitBeforeTax + totalDisallowables - totalAllowables),
  )

  const companySize = classifyCompanySize(ytdTurnover, settings)

  let citRatePercent: number
  let edtRatePercent: number
  if (companySize === 'small') {
    citRatePercent = settings.cit_rate_small
    edtRatePercent = settings.tet_rate_small
  } else if (companySize === 'medium') {
    citRatePercent = settings.cit_rate_medium
    edtRatePercent = settings.tet_rate_small  // TET small rate for non-large
  } else {
    citRatePercent = settings.cit_rate_large
    edtRatePercent = settings.tet_rate_large
  }

  const minimumTaxRatePercent = settings.minimum_tax_rate

  let citAmount = 0
  let edtAmount = 0
  if (assessableProfit > 0) {
    citAmount = round2((assessableProfit * citRatePercent) / 100)
    edtAmount = round2((assessableProfit * edtRatePercent) / 100)
  }

  const minimumTaxAmount = companySize === 'small'
    ? 0
    : round2((ytdTurnover * minimumTaxRatePercent) / 100)

  const totalTaxCharge = (citAmount + edtAmount) >= minimumTaxAmount
    ? round2(citAmount + edtAmount)
    : minimumTaxAmount

  const periodCharge = round2(totalTaxCharge - priorYtdAccrued)

  return {
    companySize,
    citRatePercent,
    edtRatePercent,
    minimumTaxRatePercent,
    totalDisallowables,
    totalAllowables,
    assessableProfit,
    citAmount,
    edtAmount,
    minimumTaxAmount,
    totalTaxCharge,
    priorYtdAccrued,
    periodCharge,
  }
}

export function endOfMonthIso(year: number, month: number): string {
  const last = new Date(Date.UTC(year, month, 0))
  const y = last.getUTCFullYear()
  const m = String(last.getUTCMonth() + 1).padStart(2, '0')
  const d = String(last.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function startOfYearIso(year: number): string {
  return `${year}-01-01`
}

export function isMonthEndIso(iso: string): boolean {
  const [y, m, d] = iso.split('-').map(s => parseInt(s, 10))
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return false
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return d === last
}
